/**
 * @fileoverview Shared run-flow dry-run step (Dry Run): boundary override,
 *               run/re-run/cancel controls, live progress, and results.
 * @module import-editor/runFlow/steps/RunDryRunStep
 *
 * Responsibilities:
 * - Render the dry-run step: jurisdiction boundary override toggle, CRS
 *   warning banner, Run/Re-run/Cancel controls, the live progress
 *   indicator, and the post-run results — the row explorer
 *   (DryRunSummaryStep) when the host adapter implements the `/rows`
 *   capability, otherwise the inline error-row table.
 * - Surface the proceed-with-errors gate and the Phase 5.5/5.6 enrichment
 *   panels when the backend emitted enrichment data.
 *
 * Dependencies:
 * - `../useImportRunFlow` — the shared state machine (via the `flow` prop).
 * - `../../components/ImportProgressIndicator`,
 *   `../../components/EnrichmentWorkflowPanel`,
 *   `../dryRunSummary/DryRunSummaryStep`,
 *   `../dryRunSummary/EnrichmentPreviewPanel` — shared run-flow UI.
 *
 * Architecture:
 * - Presentation-only; all run lifecycle transitions are hook handlers and
 *   all I/O goes through the host's services adapter. The row explorer is
 *   capability-gated on `services.listImportRunRows` so hosts without the
 *   endpoint keep the inline error table.
 *
 * Notes:
 * - Extracted from tenant-web's ImportWizardContainer (DryRunStepContent)
 *   and system-web's SystemImportWizardContainer preview case during the
 *   Phase 2A run-flow migration (G-002).
 */

import { useEffect, useMemo, useState } from 'react';
import { ImportProgressIndicator } from '../../components/ImportProgressIndicator';
import { EnrichmentWorkflowPanel, getEnrichableFields } from '../../components/EnrichmentWorkflowPanel';
import { DryRunSummaryStep } from '../dryRunSummary/DryRunSummaryStep';
import { EnrichmentPreviewPanel, type EnrichmentPreviewRow } from '../dryRunSummary/EnrichmentPreviewPanel';
import type { DryRunSummaryView } from '../dryRunSummary/types';
import type { ImportRunFlow } from '../useImportRunFlow';
import { getProgressSummaryTotals } from '../useImportRunFlow';
import type { RunFlowDryRunRow } from '../runFlowTypes';

const STATUS_BADGE: Record<RunFlowDryRunRow['status'], string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  skip:   'bg-yellow-100 text-yellow-800',
  error:  'bg-red-100 text-red-800',
};

const DRY_RUN_PAGE_SIZE = 25;

/**
 * @contract RunDryRunStepProps
 *
 * Purpose:
 * - Wire the Dry Run step to the run-flow state machine.
 *
 * Fields:
 * - flow: the `useImportRunFlow` result.
 */
export interface RunDryRunStepProps {
  flow: ImportRunFlow;
}

/**
 * @component RunDryRunStep
 *
 * Purpose:
 * - Render the run flow's Dry Run step.
 *
 * Props:
 * - RunDryRunStepProps.
 *
 * Behavior:
 * - Run Dry Run is disabled until a file upload exists and (when present)
 *   a CRS warning blocks parcel geometry comparisons in non-WGS84 sources.
 * - Cancel is armed only while a run is in flight; the backend picks up the
 *   signal at the next 500-row checkpoint.
 *
 * Data Source:
 * - Wizard state + live progress from the hook; row details are fetched
 *   lazily through the adapter's listImportRunRows when available.
 *
 * Constraints:
 * - No direct API calls; no Dexie access.
 *
 * Performance:
 * - The inline error table paginates at 25 rows; the row explorer fetches
 *   pages on demand instead of materializing the full result set.
 */
export default function RunDryRunStep({ flow }: RunDryRunStepProps) {
  const { state, patch, services } = flow;
  const s = state.dryRunSummary;
  // "Errors" in the UI = sum of every failure sub-bucket in the April 2026
  // taxonomy. No legacy `summary.failed` read — that field does not exist.
  const { failedTotal } = getProgressSummaryTotals(s);
  const hasErrors = failedTotal > 0;
  const crsWarning = state.parseResult?.crsWarning ?? null;
  const isParcelMode = state.dataMode === 'parcel';
  const running = flow.dryRunning;
  const liveProgress = flow.liveProgress;

  // Phase 5.5: per-row enrichment preview entries (owner parsing preview).
  // Derived from the dry-run summary's previewRows — only rows that were
  // evaluated carry an `enrichment` object, so the panel self-hides for
  // non-parcel modes where the backend omits the field entirely.
  const enrichmentPreviewRows: EnrichmentPreviewRow[] = useMemo(
    () => (s?.previewRows ?? [])
      .filter(r => r.enrichment)
      .map(r => ({
        rowNumber: r.rowNumber,
        matchKeyValue: r.matchKeyValue,
        enrichment: r.enrichment!,
      })),
    [s],
  );

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Run a dry-run to preview what will happen without making any changes to the database.
        {s && ' You can re-run the dry run as many times as you like — use the Re-run button below after adjusting your mappings.'}
      </p>

      {/* Jurisdiction boundary filter — interactive toggle for this run.
          Initialised from the template default on template select; the user
          may flip it here to override the template without editing it. The
          effective value is sent as `boundaryCheckEnabled` on createImportRun
          and persisted on the run doc. Disabled while a run is in flight.
          Only shown for parcel-mode imports. */}
      {isParcelMode && (
        <div
          className={`rounded-md px-3 py-2 border text-xs ${
            state.boundaryCheckEnabled
              ? 'bg-blue-50 border-blue-200 text-blue-900'
              : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}
        >
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 shrink-0"
              checked={state.boundaryCheckEnabled}
              disabled={running || !state.selectedTemplate}
              onChange={e => patch({ boundaryCheckEnabled: e.target.checked })}
            />
            <div className="space-y-0.5">
              <div className="font-medium">
                Jurisdiction filter:{' '}
                <span className={state.boundaryCheckEnabled ? 'text-blue-700' : 'text-amber-700'}>
                  {state.boundaryCheckEnabled ? 'enabled' : 'disabled'}
                </span>
                {state.boundaryCheckEnabled !== state.templateBoundaryCheckDefault && (
                  <span className="ml-2 inline-block text-[11px] font-normal italic opacity-80">
                    (overriding template default: {state.templateBoundaryCheckDefault ? 'on' : 'off'})
                  </span>
                )}
              </div>
              <div className={state.boundaryCheckEnabled ? 'text-blue-800' : 'text-amber-800'}>
                {state.boundaryCheckEnabled
                  ? <>Rows falling outside this department&apos;s district boundary will be counted as <strong>Out of Bounds</strong> and excluded. Requires a district boundary to be configured for the department; otherwise the filter is silently skipped.</>
                  : <><strong>Warning:</strong> all rows in the file will be imported regardless of location, including parcels outside the department&apos;s district. Check the box above to exclude out-of-district parcels for this run only.</>
                }
              </div>
            </div>
          </label>
        </div>
      )}

      {crsWarning && (
        <div className="rounded-md px-3 py-2 border border-red-200 bg-red-50 text-xs text-red-700">
          {crsWarning}
        </div>
      )}

      <div className="flex items-center gap-2">
        {!s && !running && (
          <button type="button" onClick={flow.handleDryRun} disabled={!state.uploadResult || !!crsWarning}
            className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed">
            Run Dry Run
          </button>
        )}
        {/* Re-run — available after a completed dry run. Resets the preview
            state and kicks off a fresh run against the current mappings so
            users can iterate on column mappings and see the impact. */}
        {s && !running && (
          <button
            type="button"
            onClick={flow.handleRerunDryRun}
            disabled={!state.uploadResult || !!crsWarning}
            className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-60"
            title="Discard these results and run the dry run again with the current mappings"
          >
            Re-run Dry Run
          </button>
        )}
        {/* Cancel — visible only while a run is in flight. The backend picks
            up the signal at the next 500-row checkpoint, so it may take a
            second or two to actually stop. */}
        {running && (
          <button
            type="button"
            onClick={flow.handleCancelDryRun}
            disabled={flow.cancellingDryRun || !state.dryRunId}
            className="px-4 py-1.5 text-xs font-medium text-red-700 border border-red-300 bg-white hover:bg-red-50 rounded-md disabled:opacity-60"
            title="Stop the dry run — you can re-run after adjusting mappings"
          >
            {flow.cancellingDryRun ? 'Cancelling…' : 'Cancel Dry Run'}
          </button>
        )}
      </div>

      {/* Live progress — shown while the dry run is in flight and kept
          visible as a frozen summary once the run completes. */}
      {(running || liveProgress.summary) && (
        <ImportProgressIndicator
          phase={running ? liveProgress.phase : (liveProgress.phase === 'failed' ? 'failed' : liveProgress.phase === 'cancelled' ? 'cancelled' : 'done')}
          summary={liveProgress.summary}
          startedAt={liveProgress.startedAt}
          frozen={!running}
          errorMessage={flow.dryRunError}
          stage={liveProgress.stage}
          stageDetail={liveProgress.stageDetail}
        />
      )}

      {!state.uploadResult && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">Upload a file first before running the dry run.</div>}
      {flow.dryRunError && !running && <div className="text-xs text-red-600">{flow.dryRunError}</div>}

      {s && state.dryRunId && !running && (
        services.listImportRunRows ? (
          // Apr 2026 Dry Run Summary — category-grouped drill-in view. Row
          // data is fetched lazily per expanded accordion via the adapter.
          <DryRunSummaryStep
            summary={(s as unknown as DryRunSummaryView)}
            runId={state.dryRunId}
            mode="dryRun"
            previewRows={enrichmentPreviewRows}
            fetchRows={(params) => services.listImportRunRows!(params.runId, {
              category: params.category,
              page: params.page,
              pageSize: params.pageSize,
              q: params.q,
              sort: params.sort,
              signal: params.signal,
            })}
            onExportCsv={services.downloadImportRunRowsCsv
              ? ({ category, q }) => {
                  if (!state.dryRunId) return;
                  services.downloadImportRunRowsCsv!(state.dryRunId, { category, q }).catch((err) => {
                    console.error('[ImportRunFlow] CSV export failed:', err);
                  });
                }
              : undefined
            }
          />
        ) : (
          <DryRunRowsTable rows={state.dryRunRows} />
        )
      )}

      {s && hasErrors && (
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
          <input type="checkbox" checked={state.proceedWithErrors} onChange={e => patch({ proceedWithErrors: e.target.checked })} className="w-3.5 h-3.5" />
          Proceed despite {failedTotal.toLocaleString()} error{failedTotal !== 1 ? 's' : ''} — error rows will be skipped
        </label>
      )}

      {/* Phase 5.6: Enrich Existing Parcels — shown after a successful dry
          run in parcel mode when the template has AI transforms. */}
      {isParcelMode && s && !hasErrors && state.selectedTemplate && services.createEnrichmentRun &&
        getEnrichableFields(state.fieldMappings).length > 0 && (
        <EnrichmentWorkflowPanel
          templateId={state.selectedTemplate._id}
          fieldMappings={state.fieldMappings}
          dataMode={state.dataMode ?? 'parcel'}
          onCreateEnrichmentRun={(params) => services.createEnrichmentRun!(params)}
          onEnrichmentRunCreated={(result) => {
            console.info('[import] Enrichment run created:', result.runId);
          }}
        />
      )}

      {/* Phase 5.5: Owner Parsing Preview & Enhancement Summary — only when
          the row explorer is NOT in use (it embeds the same panel). */}
      {!services.listImportRunRows && enrichmentPreviewRows.length > 0 && (
        <EnrichmentPreviewPanel
          previewRows={enrichmentPreviewRows}
          {...(s?.enrichmentCounts ? { enrichmentCounts: s.enrichmentCounts } : {})}
        />
      )}
    </div>
  );
}

/**
 * @component DryRunRowsTable
 *
 * Purpose:
 * - Paginated inline error-row table for hosts without the `/rows` row
 *   explorer capability.
 *
 * Props:
 * - rows: error-only dry-run rows from the run summary.
 *
 * Behavior:
 * - Paginates at DRY_RUN_PAGE_SIZE; resets to page 0 when rows change.
 *
 * Data Source:
 * - Props only — presentation-only.
 *
 * Constraints:
 * - No data access.
 *
 * Performance:
 * - Renders at most one page of rows.
 */
function DryRunRowsTable({ rows }: { rows: RunFlowDryRunRow[] }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(rows.length / DRY_RUN_PAGE_SIZE));
  const pageRows = rows.slice(page * DRY_RUN_PAGE_SIZE, (page + 1) * DRY_RUN_PAGE_SIZE);

  // Reset to page 0 whenever rows change
  useEffect(() => { setPage(0); }, [rows.length]);

  if (rows.length === 0) return null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">
          Row-level results
          <span className="ml-2 font-normal text-gray-500">
            ({rows.length.toLocaleString()} row{rows.length !== 1 ? 's' : ''})
          </span>
        </span>
        {totalPages > 1 && (
          <span className="text-[11px] text-gray-500">
            Page {page + 1} of {totalPages}
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs text-left">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700 w-12">#</th>
              <th className="px-3 py-2 font-medium text-gray-700">Match Key Value</th>
              <th className="px-3 py-2 font-medium text-gray-700 w-24">Status</th>
              <th className="px-3 py-2 font-medium text-gray-700">Reason</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => {
              const globalIdx = page * DRY_RUN_PAGE_SIZE + i;
              return (
                <tr key={globalIdx} className={i % 2 === 1 ? 'bg-gray-50/60 border-t' : 'border-t'}>
                  <td className="px-3 py-1.5 text-gray-500">{row.rowNumber}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-700 max-w-[200px] truncate">{row.matchKeyValue}</td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_BADGE[row.status]}`}>{row.status}</span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-500">{row.reason ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="px-3 py-2 border-t bg-gray-50 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-2.5 py-1 rounded-sm text-xs font-medium border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const pageNum = totalPages <= 7 ? i : (
                page < 4 ? i :
                page > totalPages - 5 ? totalPages - 7 + i :
                page - 3 + i
              );
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setPage(pageNum)}
                  className={`w-6 h-6 rounded text-[11px] font-medium transition-colors ${
                    pageNum === page
                      ? 'bg-orange-500 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            className="px-2.5 py-1 rounded-sm text-xs font-medium border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
